import type { UseQueryOptions } from '@tanstack/react-query';
import { queryOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type {
  userFilterJSONDTOSchema,
  userFilterSchema,
  userInviteFilterSchema,
  userSortableFields,
} from '@tempo-io/id-dto';

import type { z } from 'zod';
import { getIdApi } from '@/lib/api/id/getIdApi';
import type { TempoUserResponse } from '@/lib/api/types/userManagement';
import { userManagementApi } from '@/lib/api/user-management';
import { useOrgId } from '@/lib/hooks/common/useOrgId';
import { userManagementQueryKeys } from '@/lib/query-keys/userManagement';

export const useTempoUsersQuery = ({
  sortBy,
  sortOrder,
  filterBy,
  enabled = true,
}: {
  sortBy?: (typeof userSortableFields)[number];
  sortOrder?: 'asc' | 'desc';
  filterBy?: z.output<typeof userFilterJSONDTOSchema>;
  enabled?: boolean;
}) => {
  const api = getIdApi();
  return useInfiniteQuery({
    queryKey: userManagementQueryKeys.users.all({ sortBy, sortOrder, filterBy }),
    queryFn: async ({ pageParam }) => {
      const result = await api.users.v1.search.$post({
        json: {
          limit: 50,
          sortBy,
          sortOrder,
          filter: filterBy,
          cursor: pageParam ? String(pageParam) : undefined,
        },
      });
      return result.json();
    },
    enabled,
    retry: 0,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    select: (data) => data.pages.flatMap((page) => page.items),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      return lastPage.nextCursor;
    },
  });
};

export const useCountTempoUsersQuery = (filterBy?: z.infer<typeof userFilterSchema>) => {
  const orgId = useOrgId();
  return useQuery({
    queryKey: userManagementQueryKeys.users.count(orgId, filterBy),
    queryFn: () =>
      userManagementApi.getCountOfTempoUsersByOrgId({
        filterBy,
      }),
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
};

export const useCountInvitesQuery = (filterBy?: z.infer<typeof userInviteFilterSchema>) => {
  const orgId = useOrgId();
  return useQuery({
    queryKey: userManagementQueryKeys.invites.count(orgId, filterBy),
    queryFn: () =>
      userManagementApi.getCountOfPendingInvitesByOrgId({
        filterBy,
      }),
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
};

export const tempoUsersByIdsQueryOptions = (
  userIds: string[],
  options?: Omit<UseQueryOptions<TempoUserResponse[], Error>, 'queryKey' | 'queryFn'>,
) => {
  const api = getIdApi();
  const MAX_BATCH_SIZE = 30;

  return queryOptions({
    queryKey: userManagementQueryKeys.users.byIds(userIds),
    queryFn: async () => {
      if (userIds.length === 0) {
        return [];
      }

      const batches: string[][] = [];
      for (let i = 0; i < userIds.length; i += MAX_BATCH_SIZE) {
        batches.push(userIds.slice(i, i + MAX_BATCH_SIZE));
      }

      const results = await Promise.all(
        batches.map(async (batch) => {
          const result = await api.users.v1.search.$post({
            json: {
              filter: { field: 'id', operator: 'in', value: batch },
              sortBy: 'name',
              sortOrder: 'asc',
              limit: 100,
            },
          });
          const data = await result.json();
          return data.items;
        }),
      );

      return results.flat();
    },
    ...options,
    enabled: options?.enabled !== false && userIds.length > 0,
    retry: options?.retry || 0,
  });
};

export const useTempoUsersByIdsQuery = (
  userIds: string[],
  options?: Omit<UseQueryOptions<TempoUserResponse[], Error>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery(tempoUsersByIdsQueryOptions(userIds, options));
};
